import { useEffect, useState } from "react";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PasswordInput from "../../components/PasswordInput";
import { toast } from "sonner";
import { UserCog, Plus, Trash2, Lock, Unlock, Shield, Eye } from "lucide-react";

const emptyForm = { nome: "", email: "", telefone: "", senha: "", tipo: "comprador" };

export default function CotacaoAcessosPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get("/cotacao/acessos");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const criar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await cotAxios.post("/cotacao/acessos", { ...form, email: form.email.trim().toLowerCase() });
      toast.success("Acesso criado!");
      setDialogOpen(false); setForm(emptyForm);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar");
    } finally { setSaving(false); }
  };

  const toggleBloqueio = async (u) => {
    try {
      await cotAxios.put(`/cotacao/acessos/${u.id}/status`, { bloqueado: !u.bloqueado });
      toast.success(u.bloqueado ? "Desbloqueado" : "Bloqueado");
      carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const excluir = async (u) => {
    if (!window.confirm(`Excluir acesso de ${u.nome}?`)) return;
    try {
      await cotAxios.delete(`/cotacao/acessos/${u.id}`);
      toast.success("Excluído");
      carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="cot-acessos-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><UserCog className="w-6 h-6 text-blue-700" /> Acessos</h1>
          <p className="text-sm text-slate-500">Gerencie compradores e auditores da sua empresa.</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-novo-acesso">
          <Plus className="w-4 h-4 mr-1" /> Novo acesso
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Card><CardContent className="p-4 text-sm">
          <div className="font-semibold flex items-center gap-2 mb-1"><Shield className="w-4 h-4 text-blue-700" /> Comprador</div>
          <div className="text-slate-600 text-xs">Cadastra e edita produtos/fornecedores/cotações e aprova propostas. Não pode excluir cadastros.</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-sm">
          <div className="font-semibold flex items-center gap-2 mb-1"><Eye className="w-4 h-4 text-slate-700" /> Auditor</div>
          <div className="text-slate-600 text-xs">Somente visualização. Pode baixar PDFs de pedidos e mapa. Não pode criar, editar nem aprovar.</div>
        </CardContent></Card>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum acesso cadastrado ainda.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {rows.map((u) => (
            <Card key={u.id} className={u.bloqueado ? "border-red-200 opacity-70" : ""} data-testid={`acesso-${u.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold">{u.nome}</div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${u.tipo === "comprador" ? "bg-blue-100 text-blue-800" : "bg-slate-200 text-slate-700"}`}>
                      {u.tipo.toUpperCase()}
                    </span>
                    {u.bloqueado && <span className="px-2 py-0.5 rounded text-[10px] bg-red-100 text-red-700 font-bold">BLOQUEADO</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{u.email}</div>
                  {u.telefone && <div className="text-xs text-slate-500">{u.telefone}</div>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => toggleBloqueio(u)} data-testid={`btn-bloq-${u.id}`}>
                    {u.bloqueado ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => excluir(u)} data-testid={`btn-del-acesso-${u.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="dialog-novo-acesso">
          <DialogHeader>
            <DialogTitle>Novo acesso</DialogTitle>
            <DialogDescription>Cadastre um comprador ou auditor. Ele fará login em /cotacoes/login com o e-mail e senha definidos aqui.</DialogDescription>
          </DialogHeader>
          <form onSubmit={criar} className="space-y-3">
            <div>
              <Label>Tipo*</Label>
              <div className="flex gap-2 mt-1">
                {[{ v: "comprador", l: "Comprador" }, { v: "auditor", l: "Auditor" }].map((op) => (
                  <button type="button" key={op.v} onClick={() => setForm({ ...form, tipo: op.v })}
                    className={`flex-1 py-2 rounded border text-sm font-medium transition ${form.tipo === op.v ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-300"}`}
                    data-testid={`tipo-${op.v}`}
                  >{op.l}</button>
                ))}
              </div>
            </div>
            <div><Label>Nome*</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="in-nome-acesso" /></div>
            <div><Label>Email*</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="in-email-acesso" /></div>
            <div><Label>Telefone</Label><Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
            <div><Label>Senha*</Label><PasswordInput required value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} data-testid="in-senha-acesso" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-salvar-acesso">
                {saving ? "Criando..." : "Criar acesso"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
